import { Injectable } from '@angular/core';
import { Subject, combineLatest, filter, switchMap, take, takeUntil } from 'rxjs';
import { AuthenticationService, AuthenticationState } from 'services/authentication/authentication.service';
import { ConnectionState as dbConnectionState } from 'services/indexdb/class/connection-state';
import { IndexDbService } from 'services/indexdb/index-db.service';
import { ConnectionState } from 'services/socket/classes/connection-state';
import { SocketService } from 'services/socket/socket.service';
import { StorageService } from 'services/storage/storage.service';
import { TokenService } from 'services/token/token.service';
import { TranslationService } from 'services/translation/translation.service';
import { Logger } from 'src/app/common/utility/logger/logger';
import { Environment } from 'src/environments/environment';
import { Ltmi } from './class/ltmi';
import { NodeTypes } from "./class/node-types";
import { SourceDefinitionResult } from "./class/source-definition-result";
import { WorkerMessage, WorkerRequest, WorkerResult } from './definition-worker-interface';
import { SourceDefinitionStatus, TelemetryDeliveryMethod, TelemetrySemanticType, TelemetryType, TelemetryUnitOfMeasure, TreeNodeType } from "./enum";
import { IArrayNode, IDeviceName, IObjectNode, ISourceName, ISourceNode, ITelemetryNode } from "./interface";
import { DeviceStore, SourceDefinitionStore } from './stores';
import { SymanticUnitOfMeasure } from './symantic-unit-of-measure';

@Injectable({
  providedIn: 'root'
})
export class DefinitionService {
  private static unsubscribe: Subject<void> = new Subject()
  private static worker: Worker | undefined
  private static sourcesList: Array<ISourceNode> | undefined
  private static ltmiDefinitions: Array<any> | undefined
  private static sourceDefinitionStatus: Subject<SourceDefinitionResult> = new Subject()

  constructor(
    private socketService: SocketService,
    private authenticationService: AuthenticationService,
    private indexDbService: IndexDbService
  ) {
    this.listenForAuthenticationState();
  }

  public get listenForSourceDefinitionStatus(): Subject<SourceDefinitionResult> {
    return DefinitionService.sourceDefinitionStatus
  } // listenForSourceDefinitionStatus()

  private listenForAuthenticationState(): void {

    combineLatest([
      this.authenticationService.listenForAuthenticationState,
      this.socketService.ConnectionState,
      this.indexDbService.ConnectionState
    ]).pipe(
      takeUntil(DefinitionService.unsubscribe),
    ).subscribe(([authenticationState, socketState, databaseState]) => {
      if (this.initialized(authenticationState, socketState, databaseState) && this.missingDefinitions()) {
        this.startWebWorker();
      } else {
        DefinitionService.sourcesList = undefined;
        DefinitionService.ltmiDefinitions = undefined;
      }
    });
  } // listenForAuthenticationState()

  private initialized(
    authenticationState: AuthenticationState,
    socketConnectionState: ConnectionState,
    databaseConnectionState: dbConnectionState): boolean {
    return this.authenticated(authenticationState) && this.socketConnected(socketConnectionState) && this.databaseConnected(databaseConnectionState)
  }

  private missingDefinitions(): boolean {
    return !DefinitionService.sourcesList || !DefinitionService.ltmiDefinitions
  }

  private authenticated(state: AuthenticationState): boolean {
    return state === AuthenticationState.Authenticated
  }

  private socketConnected(state: ConnectionState): boolean {
    return state === ConnectionState.Connected
  }

  private databaseConnected(state: dbConnectionState)
    : boolean {
    return state === dbConnectionState.Connected
  }
  private createWorkerMessage(request: WorkerRequest, result: WorkerResult | undefined, data: any | undefined): WorkerMessage {
    return {
      request: request,
      result: result,
      data: data
    }
  } // createWorkerMessage()

  private startWebWorker(): void {
    if (typeof Worker !== 'undefined') {
      // Create Worker
      DefinitionService.worker = new Worker(new URL('./definition.worker', import.meta.url));
      DefinitionService.worker.onmessage = (event) => this.onWorkerMessage(event);
      DefinitionService.worker.onerror = (error) => this.onWorkerError(error);

      // Start the WebSocket Connection
      const tokenService: TokenService = new TokenService();
      const connect: any = {
        url: Environment.SocketUrl,
        poolId: StorageService.PoolId,
        accessToken: StorageService.AccessToken,
        authId: tokenService.AuthenticationId,
        languageCode: TranslationService.CurrentLanguage.CountryCode
      }
      DefinitionService.worker.postMessage(this.createWorkerMessage(WorkerRequest.Connect, WorkerResult.Success, connect))
    } else {
      Logger.error("Web workers are not supported in this environment.")
      // TODO: Add Foreground processing with spinner or just terminate?
      // Web workers are not supported in this environment.
    }
  } // startWebWorker()

  public static getDTCDisplayName(ltmi: any, dtc: number): string {

    try {
      if (DefinitionService.ltmiDefinitions &&
        DefinitionService.ltmiDefinitions[ltmi] &&
        DefinitionService.ltmiDefinitions[ltmi].dtcMapping &&
        DefinitionService.ltmiDefinitions[ltmi].dtcMapping[dtc]
      ) {
        return DefinitionService.ltmiDefinitions[ltmi].dtcMapping[dtc].displayName[TranslationService.CurrentLanguage.CountryCode] != undefined ?
          DefinitionService.ltmiDefinitions[ltmi].dtcMapping[dtc].displayName[TranslationService.CurrentLanguage.CountryCode] :
          DefinitionService.ltmiDefinitions[ltmi].dtcMapping[dtc].displayName['en']
      }
    } catch (error) {
      Logger.error(`SourceDefinition-getDTCDisplayName: ${ltmi} ${dtc} ${error}`)
    }

    return dtc.toString()
  } // getDTCDisplayName()


  public static getDeviceChildrenFromLTMI(ltmi: any, typeFilter: Array<TelemetryType> | undefined = undefined,
    deliveryFilter: Array<TelemetryDeliveryMethod> | undefined = undefined,
    ltmiChildren: any | undefined = undefined, arrays: Array<any> | undefined = undefined, arraySize: number | undefined = undefined): Array<ITelemetryNode> | undefined {

    if (!DefinitionService.ltmiDefinitions) {
      Logger.error("SourceDefinitionService.ltmiDefinitions Undefined")
      return undefined
    }

    let telemetryList: Array<any> = []

    try {
      if (ltmiChildren == undefined) {
        ltmiChildren = DefinitionService.ltmiDefinitions[ltmi].children
        if (ltmiChildren == undefined) {
          return telemetryList
        }
      }


      const signals = Object.keys(ltmiChildren)

      // Process Signals
      signals.forEach((signal: any) => {
        // Handle Non Array/Object items
        if (![TelemetryType.Array, TelemetryType.Object].includes(ltmiChildren[signal].type)
          && (!typeFilter || (typeFilter && typeFilter.includes(ltmiChildren[signal].type)))
          && (!deliveryFilter || (deliveryFilter && deliveryFilter.includes(ltmiChildren[signal].deliveryMethod)))) {

          const telemetryNode: ITelemetryNode = {
            typeTelemetryNode: true,
            displayName: DefinitionService.getDisplayName(ltmiChildren[signal]),
            name: signal,
            ltmi: ltmi,
            ltmiRef: ltmiChildren[signal]
          }
          telemetryList.push(Object.assign({}, telemetryNode))
        }

        // Handle Array
        if (ltmiChildren[signal].type.includes(TelemetryType.Array)) {

          const array = arrays?.find((array: any) => {
            return array[signal] ? array : undefined
          })

          const arrayNode: IArrayNode = {
            typeArrayNode: true,
            displayName: DefinitionService.getDisplayName(ltmiChildren[signal]),
            name: signal,
            ltmi: ltmi,
            ltmiRef: ltmiChildren[signal],
            arraySize: array[signal] != undefined ? array[signal] : undefined,
            parent: undefined,
            children: []
          }

          // If ComplexSchema is not Array or Object
          if (![TelemetryType.Array, TelemetryType.Object].includes(ltmiChildren[signal].complexSchema.type)
            && (!typeFilter || (typeFilter && typeFilter.includes(ltmiChildren[signal].complexSchema.type)))
            && (!deliveryFilter || (deliveryFilter && deliveryFilter.includes(ltmiChildren[signal].complexSchema.deliveryMethod)))) {
            for (let i = 0; i < array[signal]; i++) {
              arrayNode.children.push(<ITelemetryNode>{
                typeTelemetryNode: true,
                displayName: DefinitionService.getDisplayName(ltmiChildren[signal].complexSchema, i),
                name: signal + "[" + i + "]",
                ltmi: ltmi,
                ltmiRef: ltmiChildren[signal].complexSchema
              })
            } // for
          } // if

          // If ComplexSchema is Object
          if (ltmiChildren[signal].complexSchema.type.includes(TelemetryType.Object)) {
            for (let i = 0; i < array[signal]; i++) {
              arrayNode.children.push(<IObjectNode>{
                typeObjectNode: true,
                displayName: DefinitionService.getDisplayName(ltmiChildren[signal].complexSchema, i),
                name: signal + "[" + i + "]",
                arrayPos: i,
                ltmi: ltmi,
                ltmiRef: ltmiChildren[signal].complexSchema,
                parent: ltmiChildren[signal],
                children: DefinitionService.getDeviceChildrenFromLTMI(ltmi, typeFilter, deliveryFilter, ltmiChildren[signal].complexSchema.complexSchema, arrays, array[signal])
              })
            } // for

          } // If Array Object

          // If there is at least one child, then push the array
          if (arrayNode.children.length) {
            telemetryList.push(Object.assign({}, arrayNode))
          }
        } // If Array

        // TBD Object Node Here

      }) // For Signals List

    } catch (error) {
      Logger.error(`SourceDefinition getDeviceChildrenFromLTMI: ${ltmi} ${error}`)
    }
    return telemetryList;
  } // getDeviceChildrenFromLTMI

  private static getDisplayName(ltmiRef: any, arrayPosition: number | undefined = undefined): string {
    const pos = (arrayPosition != undefined) ? "[" + (arrayPosition) + "]" : ''
    return ltmiRef.displayName[TranslationService.CurrentLanguage.CountryCode] != undefined ?
      ltmiRef.displayName[TranslationService.CurrentLanguage.CountryCode] + pos : ltmiRef.displayName['en'] + pos
  }  // getDisplayName()

  // Process Messages from Worker Thread
  private onWorkerMessage(event: any): void {
    const message: WorkerMessage = event.data;
    if (message)
      switch (message.request) {
        case WorkerRequest.Complete:
          this.indexDbService.ConnectionState.pipe(
            filter((state: dbConnectionState) => state === dbConnectionState.Connected),
            switchMap(() => {
              return combineLatest([
                this.indexDbService.all(DeviceStore).pipe(take(1)),
                this.indexDbService.all(SourceDefinitionStore).pipe(take(1))]
              )
            }),
            take(1)
          )
            .subscribe(([ltmis, sourceDefinitions]) => {
              const ltmiCount = !!ltmis ? Object.keys(ltmis).length : 0

              sourceDefinitions?.length ? DefinitionService.sourcesList = sourceDefinitions : Logger.debug("No source definitions loaded")
              ltmiCount ? DefinitionService.ltmiDefinitions = Ltmi.toObject(ltmis) : Logger.debug(`No devices loaded`)

              DefinitionService.sourceDefinitionStatus.next({
                status: ltmiCount && sourceDefinitions?.length ? SourceDefinitionStatus.Success : SourceDefinitionStatus.Failed
              })
            })
          break;
        case WorkerRequest.Error:
          Logger.error(message.data)
          break;
        default:
          Logger.error("Unknown Worker Response")
      }

    // We are done so kill the worker
    if (!Environment.isDevelopment && !!DefinitionService.worker) {
      DefinitionService.worker.terminate()
      DefinitionService.worker = undefined
    }
  } // onWorkerMessage

  private onWorkerError(error: any): void {
    Logger.error(`onWorkerError: ${error}`)
  } // onWorkerError()

  public static get SourcesListReady(): boolean {
    return DefinitionService.sourcesList !== undefined;
  } // get isSourcesListReady()

  public static getSourcesList(): Array<ISourceNode> {
    return structuredClone(DefinitionService.sourcesList ?? []);
  } // getSourcesList()

  public static getSourceNames(): Array<ISourceName> {
    if (DefinitionService.SourcesListReady && DefinitionService.sourcesList) {
      let sources: Array<ISourceName> = [];
      DefinitionService.sourcesList.forEach(source => {
        sources.push({
          sourceId: source.name,
          displayName: source.displayName
        })
      });
      return sources;
    }
    return []
  } // getSourceNames()

  public static getSourceDeviceList(sourceId: string): IDeviceName {
    if (DefinitionService.SourcesListReady && DefinitionService.sourcesList) {
      // Find the source in the tree
      const source = DefinitionService.sourcesList.find(source => source.name == sourceId);
      if (!source || !source.children?.length)
        return {}

      DefinitionService.devices = {}
      return DefinitionService.iterateSourceForDevices(source.children);
    }
    return {};
  } // getSourceDeviceList()

  private static devices: IDeviceName = {};
  private static iterateSourceForDevices(source: Array<any>): IDeviceName {
    source.forEach((item: any) => {
      if (NodeTypes.getNodeType(item) != TreeNodeType.Device) {
        this.iterateSourceForDevices(item.children)
      } else {
        DefinitionService.devices[item.name] = item.displayName
      }
    })
    return DefinitionService.devices;
  } // iterateSourceForDevices()

  public static getUoMSymbol(semanticType: TelemetrySemanticType, uom: TelemetryUnitOfMeasure): string {
    if (!semanticType || !uom)
      return ""

    const found = SymanticUnitOfMeasure.find(item => item.symanticType.toLowerCase() == semanticType.toLocaleLowerCase())
    if (found) {
      const result = found.UoM.find(item => item.unit.toLocaleLowerCase() == uom.toLocaleLowerCase())
      if (result) {
        return result.symbol
      }
    }

    return ""
  } // getUoMSymbol()
} // class DefinitionService

